import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../../services/api';
import { hasPermission } from '../common/permissions';

const emptyForm = {
  loss_date: new Date().toISOString().slice(0, 10),
  event_title: '',
  description: '',
  department_id: '',
  risk_id: '',
  she_event_id: '',
  financial_impact: 0,
  non_financial_impact: '',
  root_cause: '',
  status: 'Open',
  evidence: ''
};

const emptyLCForm = {
  loss_date: new Date().toISOString().slice(0, 10),
  event_title: '',
  description: '',
  department_id: '',
  priority_level: '',
  complainant: '',
  accused_person: '',
  time_of_occurrence: '',
  case_against: '',
  police_ref: '',
  police_report_file: null,
  case_category: '',
  misconduct_type: '',
  location: '',
  property_involved: '',
  estimate_value: '',
  estimated_loss_value: '',
  estimated_recovery_value: '',
  corrective_action: '',
  corrective_action_recommendation: '',
  corrective_action_taken: '',
  action_owner: '',
  quarter: '',
  case_prefix: 'LC',
  status: 'Open',
};

const LossEvents = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [metadata, setMetadata] = useState({ risks: [], sheEvents: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  // Tab and Loss Control state
  const [activeTab, setActiveTab] = useState(0);
  const [lcDialogOpen, setLcDialogOpen] = useState(false);
  const [lcForm, setLcForm] = useState(emptyLCForm);
  const [lcEditing, setLcEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const canAdd = hasPermission(user, 'Loss Events', 'add');
  const canEdit = hasPermission(user, 'Loss Events', 'edit');
  const canDelete = hasPermission(user, 'Loss Events', 'delete');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, metaRes] = await Promise.all([
        api.get('/loss-events'),
        api.get('/loss-metadata')
      ]);
      setEvents(eventsRes.data || []);
      setMetadata(metaRes.data || { risks: [], sheEvents: [], departments: [] });
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to load loss events.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const departmentName = (id) => metadata.departments?.find(d => d.id === id)?.name || id || 'Unassigned';

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return events.filter(event =>
      event.loss_reference?.toLowerCase().includes(term) ||
      event.event_title?.toLowerCase().includes(term) ||
      event.root_cause?.toLowerCase().includes(term) ||
      departmentName(event.department_id)?.toLowerCase().includes(term)
    );
  }, [events, search, metadata.departments]);

  const totals = useMemo(() => {
    const total = events.reduce((sum, item) => sum + Number(item.financial_impact || 0), 0);
    const open = events.filter(item => !['Closed', 'Resolved'].includes(item.status)).length;
    const linked = events.filter(item => item.risk_id || item.she_event_id).length;
    return { total, open, linked };
  }, [events]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setForm({
      loss_date: event.loss_date ? event.loss_date.slice(0, 10) : emptyForm.loss_date,
      event_title: event.event_title || '',
      description: event.description || '',
      department_id: event.department_id || '',
      risk_id: event.risk_id || '',
      she_event_id: event.she_event_id || '',
      financial_impact: event.financial_impact || 0,
      non_financial_impact: event.non_financial_impact || '',
      root_cause: event.root_cause || '',
      status: event.status || 'Open',
      evidence: event.evidence || ''
    });
    setDialogOpen(true);
  };

  const today = new Date().toISOString().slice(0, 10);
  const nowTime = new Date().toTimeString().slice(0, 5);

  const isValidText = (val, min = 3) => val && val.trim().length >= min;

  const save = async () => {
    if (saving) return;

    if (!isValidText(form.event_title)) {
      setNotice({ open: true, message: 'Event Title must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (form.loss_date > today) {
      setNotice({ open: true, message: 'Loss Date cannot be in the future.', severity: 'error' });
      return;
    }
    if (Number(form.financial_impact || 0) < 0) {
      setNotice({ open: true, message: 'Actual Financial Loss cannot be negative.', severity: 'error' });
      return;
    }
    if (form.description && !isValidText(form.description)) {
      setNotice({ open: true, message: 'Description must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (form.root_cause && !isValidText(form.root_cause)) {
      setNotice({ open: true, message: 'Root Cause must be at least 3 characters.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        risk_id: form.risk_id || null,
        she_event_id: form.she_event_id || null,
        department_id: form.department_id || null,
        financial_impact: Number(form.financial_impact || 0)
      };
      const response = editing
        ? await api.put(`/loss-events/${editing.loss_id}`, payload)
        : await api.post('/loss-events', payload);
      const savedEvent = response.data?.event;
      if (savedEvent) {
        setEvents(current => editing
          ? current.map(item => item.loss_id === savedEvent.loss_id ? savedEvent : item)
          : [savedEvent, ...current]
        );
      }
      setNotice({ open: true, message: editing ? 'Loss event updated.' : 'Loss event recorded.', severity: 'success' });
      setDialogOpen(false);
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to save loss event.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/loss-events/${deleteTarget.loss_id}`);
      setEvents(current => current.filter(item => item.loss_id !== deleteTarget.loss_id));
      setNotice({ open: true, message: 'Loss event deleted.', severity: 'success' });
      setDeleteTarget(null);
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to delete loss event.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

  const fieldSx = {
    '& .MuiInputBase-root': {
      color: '#f8fafc',
      background: 'rgba(15, 23, 42, 0.45)'
    },
    '& .MuiInputLabel-root': {
      color: '#cbd5e1'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#f8fafc'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(203, 213, 225, 0.28)'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(248, 250, 252, 0.45)'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981'
    },
    '& .MuiSelect-icon, & input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
      color: '#f8fafc'
    }
  };

  const selectMenuProps = {
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: '#1e293b',
          color: '#f8fafc',
          border: '1px solid rgba(255,255,255,0.12)'
        }
      }
    }
  };

  // Loss Control helpers
  const lossControlEvents = useMemo(() => events.filter(e => e.record_type === 'loss_control'), [events]);
  const lossEvents = useMemo(() => events.filter(e => e.record_type !== 'loss_control'), [events]);

  const filteredLC = useMemo(() => {
    const term = search.toLowerCase();
    return lossControlEvents.filter(event =>
      event.case_number?.toLowerCase().includes(term) ||
      event.complainant?.toLowerCase().includes(term) ||
      event.event_title?.toLowerCase().includes(term) ||
      event.case_category?.toLowerCase().includes(term) ||
      event.location?.toLowerCase().includes(term) ||
      departmentName(event.department_id)?.toLowerCase().includes(term)
    );
  }, [lossControlEvents, search, metadata.departments]);

  const filteredLossEvents = useMemo(() => {
    const term = search.toLowerCase();
    return lossEvents.filter(event =>
      event.loss_reference?.toLowerCase().includes(term) ||
      event.event_title?.toLowerCase().includes(term) ||
      event.root_cause?.toLowerCase().includes(term) ||
      departmentName(event.department_id)?.toLowerCase().includes(term)
    );
  }, [lossEvents, search, metadata.departments]);

  const openLCCreate = () => {
    setLcEditing(null);
    setLcForm(emptyLCForm);
    setLcDialogOpen(true);
  };

  const openLCEdit = (event) => {
    setLcEditing(event);
    setLcForm({
      loss_date: event.loss_date ? event.loss_date.slice(0, 10) : emptyLCForm.loss_date,
      event_title: event.event_title || '',
      description: event.description || '',
      department_id: event.department_id || '',
      priority_level: event.priority_level || '',
      complainant: event.complainant || '',
      accused_person: event.accused_person || '',
      time_of_occurrence: event.time_of_occurrence || '',
      case_against: event.case_against || '',
      police_ref: event.police_ref || '',
      police_report_file: null,
      case_category: event.case_category || '',
      misconduct_type: event.misconduct_type || '',
      location: event.location || '',
      property_involved: event.property_involved || '',
      estimate_value: event.estimate_value || '',
      estimated_loss_value: event.estimated_loss_value || '',
      estimated_recovery_value: event.estimated_recovery_value || '',
      corrective_action: event.corrective_action || '',
      corrective_action_recommendation: event.corrective_action_recommendation || '',
      corrective_action_taken: event.corrective_action_taken || '',
      action_owner: event.action_owner || '',
      quarter: event.quarter || '',
      case_prefix: event.case_prefix || 'LC',
      status: event.status || 'Open',
    });
    setLcDialogOpen(true);
  };

  const saveLCCase = async () => {
    if (saving) return;

    if (!isValidText(lcForm.event_title) && !isValidText(lcForm.description)) {
      setNotice({ open: true, message: 'Case Brief / Title or Description must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (!lcForm.loss_date) {
      setNotice({ open: true, message: 'Date of Occurrence is required.', severity: 'error' });
      return;
    }
    if (lcForm.loss_date > today) {
      setNotice({ open: true, message: 'Date of Occurrence cannot be in the future.', severity: 'error' });
      return;
    }
    if (lcForm.loss_date === today && lcForm.time_of_occurrence && lcForm.time_of_occurrence > nowTime) {
      setNotice({ open: true, message: 'Time of Occurrence cannot be in the future.', severity: 'error' });
      return;
    }
    if (lcForm.complainant && !isValidText(lcForm.complainant)) {
      setNotice({ open: true, message: 'Complainant name must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (lcForm.accused_person && !isValidText(lcForm.accused_person)) {
      setNotice({ open: true, message: 'Accused Person must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (lcForm.location && !isValidText(lcForm.location)) {
      setNotice({ open: true, message: 'Location must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (lcForm.description && !isValidText(lcForm.description)) {
      setNotice({ open: true, message: 'Description must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (lcForm.corrective_action && !isValidText(lcForm.corrective_action)) {
      setNotice({ open: true, message: 'Corrective Action must be at least 3 characters.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Use FormData for file upload support
      const payload = new FormData();
      payload.append('record_type', 'loss_control');
      payload.append('loss_date', lcForm.loss_date);
      payload.append('event_title', lcForm.event_title || lcForm.description?.slice(0, 200) || 'Loss Control Case');
      payload.append('description', lcForm.description || '');
      payload.append('department_id', lcForm.department_id || '');
      payload.append('priority_level', lcForm.priority_level || '');
      payload.append('complainant', lcForm.complainant || '');
      payload.append('accused_person', lcForm.accused_person || '');
      payload.append('time_of_occurrence', lcForm.time_of_occurrence || '');
      payload.append('case_against', lcForm.case_against || '');
      payload.append('police_ref', lcForm.police_ref || '');
      payload.append('case_category', lcForm.case_category || '');
      payload.append('misconduct_type', lcForm.misconduct_type || '');
      payload.append('location', lcForm.location || '');
      payload.append('property_involved', lcForm.property_involved || '');
      payload.append('estimate_value', lcForm.estimate_value ? Number(lcForm.estimate_value) : '');
      payload.append('estimated_loss_value', lcForm.estimated_loss_value ? Number(lcForm.estimated_loss_value) : '');
      payload.append('estimated_recovery_value', lcForm.estimated_recovery_value ? Number(lcForm.estimated_recovery_value) : '');
      payload.append('corrective_action', lcForm.corrective_action || '');
      payload.append('corrective_action_recommendation', lcForm.corrective_action_recommendation || '');
      payload.append('corrective_action_taken', lcForm.corrective_action_taken || '');
      payload.append('action_owner', lcForm.action_owner || '');
      payload.append('quarter', lcForm.quarter || '');
      payload.append('case_prefix', lcForm.case_prefix || 'LC');
      payload.append('status', lcForm.status || 'Open');
      payload.append('financial_impact', lcForm.estimate_value ? Number(lcForm.estimate_value) : 0);
      
      // Append file if selected
      if (lcForm.police_report_file && lcForm.police_report_file instanceof File) {
        payload.append('police_report_file', lcForm.police_report_file);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };

      const response = lcEditing
        ? await api.put(`/loss-events/${lcEditing.loss_id}`, payload, config)
        : await api.post('/loss-events', payload, config);
      const savedEvent = response.data?.event;
      if (savedEvent) {
        setEvents(current => lcEditing
          ? current.map(item => item.loss_id === savedEvent.loss_id ? savedEvent : item)
          : [savedEvent, ...current]
        );
      }
      setNotice({ open: true, message: lcEditing ? 'Case updated.' : 'Loss control case recorded.', severity: 'success' });
      setLcDialogOpen(false);
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to save case.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/loss-events/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNotice({ open: true, message: response.data?.message || 'Import complete.', severity: 'success' });
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Import failed.', severity: 'error' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const priorityColor = (level) => {
    const map = { Extreme: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#10b981' };
    return map[level] || '#94a3b8';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#10b981' }} /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>Loss Events</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.04)' } }} />
          {activeTab === 0 && canAdd && <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 'bold', bgcolor: '#10b981' }}>Record Loss</Button>}
          {activeTab === 1 && canAdd && (
            <>
              <Button variant="contained" onClick={openLCCreate} sx={{ textTransform: 'none', fontWeight: 'bold', bgcolor: '#3b82f6' }}>Record Case</Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                disabled={importing}
                sx={{ textTransform: 'none', fontWeight: 'bold', color: '#f59e0b', borderColor: 'rgba(245,158,11,0.5)', '&:hover': { borderColor: '#f59e0b', background: 'rgba(245,158,11,0.08)' }, '&.Mui-disabled': { color: '#f59e0b', borderColor: 'rgba(245,158,11,0.4)', opacity: 0.8 } }}
              >
                {importing ? 'Importing...' : 'Import CSV/Excel'}
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileImport} />
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontWeight: 'bold', fontSize: '0.9rem', '&.Mui-selected': { color: 'white' } },
            '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: '3px' }
          }}
        >
          <Tab label={`Loss Events (${lossEvents.length})`} />
          <Tab label={`Loss Control Cases (${lossControlEvents.length})`} />
        </Tabs>
      </Box>

      {/* Tab 0: Loss Events */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              ['Total Actual Loss', money(totals.total), '#ef4444'],
              ['Open Loss Events', totals.open, '#f59e0b'],
              ['Linked to Risk/SHE', totals.linked, '#3b82f6']
            ].map(([label, value, color]) => (
              <Grid item xs={12} md={4} key={label}>
                <Paper className="glass" sx={{ p: 2.5, borderRadius: '16px', background: 'rgba(30,41,59,0.45)' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{label}</Typography>
                  <Typography sx={{ color, fontSize: '1.5rem', fontWeight: 'bold' }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TableContainer component={Paper} sx={{ background: 'rgba(15,23,42,0.75)', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { color: '#94a3b8', fontWeight: 'bold' } }}>
                  <TableCell>Reference</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Actual Financial Loss</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Linked Records</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLossEvents.map(event => (
                  <TableRow key={event.loss_id} sx={{ '& td': { color: '#f1f5f9', borderColor: 'rgba(255,255,255,0.05)' } }}>
                    <TableCell sx={{ color: '#10b981 !important', fontWeight: 'bold' }}>{event.loss_reference}</TableCell>
                    <TableCell>{event.loss_date?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{event.event_title}</Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{event.root_cause || 'Root cause not captured'}</Typography>
                    </TableCell>
                    <TableCell>{departmentName(event.department_id)}</TableCell>
                    <TableCell>{money(event.financial_impact)}</TableCell>
                    <TableCell><Chip size="small" label={event.status} sx={{ color: event.status === 'Closed' ? '#10b981' : '#f59e0b', bgcolor: 'rgba(255,255,255,0.05)', fontWeight: 'bold' }} /></TableCell>
                    <TableCell sx={{ color: '#94a3b8 !important' }}>{event.risk?.sn || event.she_event?.action_id || 'None'}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                        {canEdit && (
                          <Button variant="outlined" size="small" onClick={() => openEdit(event)} sx={{ color: '#3b82f6', borderColor: 'rgba(59,130,246,0.55)', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', minWidth: 72, '&:hover': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.1)' } }}>Edit</Button>
                        )}
                        {canDelete && (
                          <Button variant="outlined" size="small" onClick={() => setDeleteTarget(event)} sx={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.55)', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', minWidth: 88, '&:hover': { borderColor: '#ef4444', background: 'rgba(239,68,68,0.1)' } }}>Delete</Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLossEvents.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ color: '#64748b', py: 6 }}>No loss events found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Tab 1: Loss Control Cases */}
      {activeTab === 1 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              ['Total Cases', lossControlEvents.length, '#3b82f6'],
              ['Open Cases', lossControlEvents.filter(e => e.status === 'Open' || e.status === 'In Progress').length, '#f59e0b'],
              ['Completed', lossControlEvents.filter(e => e.status === 'Completed' || e.status === 'Closed').length, '#10b981']
            ].map(([label, value, color]) => (
              <Grid item xs={12} md={4} key={label}>
                <Paper className="glass" sx={{ p: 2.5, borderRadius: '16px', background: 'rgba(30,41,59,0.45)' }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{label}</Typography>
                  <Typography sx={{ color, fontSize: '1.5rem', fontWeight: 'bold' }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TableContainer component={Paper} sx={{ background: 'rgba(15,23,42,0.75)', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem' } }}>
                  <TableCell>Case No.</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Complainant</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLC.map(event => (
                  <TableRow key={event.loss_id} sx={{ '& td': { color: '#f1f5f9', borderColor: 'rgba(255,255,255,0.05)', fontSize: '0.85rem' } }}>
                    <TableCell sx={{ color: '#10b981 !important', fontWeight: 'bold' }}>{event.case_number || event.loss_reference}</TableCell>
                    <TableCell>
                      {event.priority_level && (
                        <Chip size="small" label={event.priority_level} sx={{ color: priorityColor(event.priority_level), bgcolor: `${priorityColor(event.priority_level)}15`, border: `1px solid ${priorityColor(event.priority_level)}40`, fontWeight: 'bold', fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>{event.complainant || '—'}</TableCell>
                    <TableCell>{event.loss_date?.slice(0, 10)}</TableCell>
                    <TableCell>{event.case_category || '—'}</TableCell>
                    <TableCell>{departmentName(event.department_id)}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>{event.location || '—'}</TableCell>
                    <TableCell><Chip size="small" label={event.status} sx={{ color: event.status === 'Completed' || event.status === 'Closed' ? '#10b981' : '#f59e0b', bgcolor: 'rgba(255,255,255,0.05)', fontWeight: 'bold', fontSize: '0.7rem' }} /></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {canEdit && (
                          <Button variant="outlined" size="small" onClick={() => openLCEdit(event)} sx={{ color: '#3b82f6', borderColor: 'rgba(59,130,246,0.55)', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', minWidth: 60, '&:hover': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.1)' } }}>Edit</Button>
                        )}
                        {canDelete && (
                          <Button variant="outlined" size="small" onClick={() => setDeleteTarget(event)} sx={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.55)', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', minWidth: 72, '&:hover': { borderColor: '#ef4444', background: 'rgba(239,68,68,0.1)' } }}>Delete</Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLC.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ color: '#64748b', py: 6 }}>No loss control cases found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Loss Event Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{editing ? `Edit ${editing.loss_reference}` : 'Record Loss Event'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Event Title" value={form.event_title} onChange={(e) => setForm({ ...form, event_title: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Loss Date" InputLabelProps={{ shrink: true }} inputProps={{ max: today }} value={form.loss_date} onChange={(e) => setForm({ ...form, loss_date: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Actual Financial Loss" value={form.financial_impact} inputProps={{ min: 0 }} onChange={(e) => setForm({ ...form, financial_impact: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth label="Department" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>{metadata.departments?.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Linked Risk" value={form.risk_id} onChange={(e) => setForm({ ...form, risk_id: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                <MenuItem value="">None</MenuItem>
                {metadata.risks?.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.sn} - {r.risk_description?.length > 70 ? `${r.risk_description.slice(0, 70)}...` : r.risk_description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Linked SHE Event" value={form.she_event_id} onChange={(e) => setForm({ ...form, she_event_id: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                <MenuItem value="">None</MenuItem>
                {metadata.sheEvents?.map(s => {
                  const sheDescription = s.description || s.activity_category || 'SHE record';
                  return (
                    <MenuItem key={s.id} value={s.id}>
                      {s.action_id} - {s.activity_category} - {sheDescription.length > 55 ? `${sheDescription.slice(0, 55)}...` : sheDescription}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}><TextField select fullWidth label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>{['Open', 'Under Review', 'Resolved', 'Closed'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="Root Cause" value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Non-financial Impact" value={form.non_financial_impact} onChange={(e) => setForm({ ...form, non_financial_impact: e.target.value })} sx={fieldSx} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 'bold', '&.Mui-disabled': { bgcolor: 'rgba(16,185,129,0.45)', color: '#d1fae5' } }}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Loss Control Case Dialog */}
      <Dialog open={lcDialogOpen} onClose={() => setLcDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{lcEditing ? `Edit ${lcEditing.case_number || lcEditing.loss_reference}` : 'Record Loss Control Case'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Case Number Prefix */}
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Case Prefix" value={lcForm.case_prefix} onChange={(e) => setLcForm({ ...lcForm, case_prefix: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['LC', ' Case', 'INC'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Case Brief / Title" value={lcForm.event_title} onChange={(e) => setLcForm({ ...lcForm, event_title: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Date of Occurrence" InputLabelProps={{ shrink: true }} inputProps={{ max: today }} value={lcForm.loss_date} onChange={(e) => setLcForm({ ...lcForm, loss_date: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth type="time" label="Time of Occurrence" InputLabelProps={{ shrink: true }} value={lcForm.time_of_occurrence} onChange={(e) => setLcForm({ ...lcForm, time_of_occurrence: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Priority Level" value={lcForm.priority_level} onChange={(e) => setLcForm({ ...lcForm, priority_level: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['Low', 'Medium', 'High', 'Extreme'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Case Against" value={lcForm.case_against} onChange={(e) => setLcForm({ ...lcForm, case_against: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['STAFF', 'UNIVERSITY', 'STUDENT', 'OTHER'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Case Category" value={lcForm.case_category} onChange={(e) => setLcForm({ ...lcForm, case_category: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['BURGLARY', 'THEFT', 'INCIDENTS', 'FRAUD', 'ASSAULT', 'VANDALISM', 'OTHER'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Quarter" value={lcForm.quarter} onChange={(e) => setLcForm({ ...lcForm, quarter: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['First Quarter', 'Second Quarter', 'Third Quarter', 'Fourth Quarter'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            {/* Misconduct Type - Only show when case category is OTHER */}
            {lcForm.case_category === 'OTHER' && (
              <Grid item xs={12} md={3}>
                <TextField select fullWidth label="Misconduct Type" value={lcForm.misconduct_type} onChange={(e) => setLcForm({ ...lcForm, misconduct_type: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                  {['Staff Misconduct', 'Student Misconduct'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={6}><TextField fullWidth label="Name of Complainant" value={lcForm.complainant} onChange={(e) => setLcForm({ ...lcForm, complainant: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Accused Person" value={lcForm.accused_person} onChange={(e) => setLcForm({ ...lcForm, accused_person: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Police Reference" value={lcForm.police_ref} onChange={(e) => setLcForm({ ...lcForm, police_ref: e.target.value })} sx={fieldSx} />
            </Grid>
            {/* Police Report File Upload */}
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ 
                  textTransform: 'none', 
                  borderColor: lcForm.police_report_file ? '#10b981' : 'rgba(203, 213, 225, 0.28)',
                  color: lcForm.police_report_file ? '#10b981' : '#cbd5e1',
                  '&:hover': { borderColor: '#10b981', background: 'rgba(16,185,129,0.08)' }
                }}
              >
                {lcForm.police_report_file ? (lcForm.police_report_file.name || 'File Selected') : 'Upload Police Report'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setLcForm({ ...lcForm, police_report_file: e.target.files[0] })}
                />
              </Button>
              {lcEditing?.police_report_file && !lcForm.police_report_file && (
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>File already uploaded</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Affected Department" value={lcForm.department_id} onChange={(e) => setLcForm({ ...lcForm, department_id: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {metadata.departments?.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Status" value={lcForm.status} onChange={(e) => setLcForm({ ...lcForm, status: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['Open', 'In Progress', 'Completed', 'Closed'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Location / Work Area" value={lcForm.location} onChange={(e) => setLcForm({ ...lcForm, location: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Property Involved" value={lcForm.property_involved} onChange={(e) => setLcForm({ ...lcForm, property_involved: e.target.value })} sx={fieldSx} /></Grid>
            
            {/* Financial Tracking - Estimated Loss vs Recovery */}
            <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Estimated Loss Value" value={lcForm.estimated_loss_value} inputProps={{ min: 0 }} onChange={(e) => setLcForm({ ...lcForm, estimated_loss_value: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Estimated Recovery Value" value={lcForm.estimated_recovery_value} inputProps={{ min: 0 }} onChange={(e) => setLcForm({ ...lcForm, estimated_recovery_value: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                fullWidth 
                type="number" 
                label="Net Loss (Difference)" 
                value={lcForm.estimated_loss_value && lcForm.estimated_recovery_value ? 
                  Number(lcForm.estimated_loss_value) - Number(lcForm.estimated_recovery_value) : ''} 
                InputProps={{ readOnly: true }}
                sx={{ 
                  ...fieldSx, 
                  '& .MuiInputBase-root': { background: 'rgba(15, 23, 42, 0.65)', color: '#f8fafc' },
                  '& .MuiInputLabel-root': { color: '#94a3b8' }
                }} 
              />
            </Grid>
            <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Estimate Value (Legacy)" value={lcForm.estimate_value} inputProps={{ min: 0 }} onChange={(e) => setLcForm({ ...lcForm, estimate_value: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="Action Owner" value={lcForm.action_owner} onChange={(e) => setLcForm({ ...lcForm, action_owner: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Case Brief / Description" value={lcForm.description} onChange={(e) => setLcForm({ ...lcForm, description: e.target.value })} sx={fieldSx} /></Grid>
            {/* Separate Corrective Action Fields */}
            <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={2} label="Corrective Action Recommendation" value={lcForm.corrective_action_recommendation} onChange={(e) => setLcForm({ ...lcForm, corrective_action_recommendation: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={2} label="Corrective Action Taken" value={lcForm.corrective_action_taken} onChange={(e) => setLcForm({ ...lcForm, corrective_action_taken: e.target.value })} sx={fieldSx} /></Grid>
            {/* Legacy field for backward compatibility */}
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Corrective Action (Legacy - Combined)" value={lcForm.corrective_action} onChange={(e) => setLcForm({ ...lcForm, corrective_action: e.target.value })} sx={{ ...fieldSx, '& .MuiInputLabel-root': { color: '#64748b' } }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setLcDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveLCCase} disabled={saving} sx={{ bgcolor: '#3b82f6', textTransform: 'none', fontWeight: 'bold', '&.Mui-disabled': { bgcolor: 'rgba(59,130,246,0.45)', color: '#dbeafe' } }}>{saving ? 'Saving...' : 'Save Case'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', p: 1, minWidth: 360 } }}
      >
        <DialogTitle sx={{ color: '#ef4444', fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#cbd5e1', mb: 2 }}>Delete this record? This action cannot be undone.</Typography>
          {deleteTarget && (
            <Box sx={{ p: 2, borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Typography sx={{ color: '#10b981', fontWeight: 'bold', fontSize: '0.9rem' }}>{deleteTarget.case_number || deleteTarget.loss_reference}</Typography>
              <Typography sx={{ color: '#f8fafc', fontSize: '0.85rem', mt: 0.5 }}>{deleteTarget.event_title}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={remove} disabled={deleting} sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: '#dc2626' } }}>
            {deleting ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={notice.open} autoHideDuration={4000} onClose={() => setNotice({ ...notice, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity={notice.severity}
          variant="filled"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.9rem',
            color: '#fff',
            bgcolor: notice.severity === 'error' ? '#ef4444' : '#10b981',
            '& .MuiAlert-icon': { color: '#fff' }
          }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LossEvents;
